// ============================================
// P2 E-02：全局 AI 帮助助手浮窗
// - 右下角悬浮按钮（"?" 圈圈 Tooltip：作用/机制/使用/注意）
// - 抽屉：会话管理（新建/切换/删除）+ 消息流（SSE 流式）+ 引用来源 + 预设问题
// - 错误态：配额超限(403)/未启用(503)/网络失败
// ============================================
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Drawer, Dropdown, Empty, Input, Popconfirm, Select, Space, Spin, Tooltip, Typography, message,
} from 'antd';
import {
  DeleteOutlined, HistoryOutlined, MessageOutlined, PlusOutlined, QuestionCircleOutlined,
  SendOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAiStore } from '../../stores/aiStore';

const { Text, Paragraph } = Typography;

const PRESET_QUESTIONS = [
  '大屏编辑器怎么添加组件？',
  '支持哪些图表组件？',
  '怎么绑定数据源/数据集？',
  '设备离线怎么排查？',
  '如何发布大屏并预览？',
];

export default function AiAssistantWidget() {
  const navigate = useNavigate();
  const {
    status, open, sessions, currentSessionId, messages, sending, streamingText, error, quotaExceeded,
    init, setOpen, newSession, selectSession, removeSession, send, clearError,
  } = useAiStore();
  const [input, setInput] = useState('');
  const [preset, setPreset] = useState<string | undefined>(undefined);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => { init(); }, [init]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, streamingText, open]);

  const enabled = status?.enabled === true;
  const quotaText = useMemo(() => {
    if (!status) return '';
    const q = status.quota;
    return q.limit < 0 ? 'AI 问答：不限' : `AI 问答：今日 ${q.used}/${q.limit}`;
  }, [status]);
  // 配额为 0（套餐不含）或已用完 → 主动提示并禁用发送（避免"发送后无回复"的困惑）
  const quotaZero = !!status?.quota && status.quota.limit === 0;
  const quotaBlocked = !!status?.quota && status.quota.limit >= 0 && status.quota.remaining <= 0;

  const doSend = (text?: string) => {
    const t = (text ?? input).trim();
    if (!t || quotaBlocked) return;
    setInput('');
    setPreset(undefined);
    send(t);
  };

  if (!enabled) return null;

  return (
    <>
      <Tooltip
        placement="left"
        title={(
          <div style={{ maxWidth: 300, fontSize: 12, lineHeight: 1.7 }}>
            <b>AI 帮助助手</b><br />
            <b>作用：</b>基于平台知识库回答使用问题，可查场景/设备/数据（只读）。<br />
            <b>机制：</b>问题 → 知识库检索（RAG）→ AI 生成，答复附引用来源。<br />
            <b>使用：</b>点击打开 → 选预设问题或直接输入 → 回车发送；会话自动保存，可切换/删除。<br />
            <b>注意：</b>按套餐限额（{quotaText || '—'}）；答复以官方文档为准，不替代人工支持。
          </div>
        )}
      >
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<MessageOutlined />}
          data-testid="ai-assistant-fab"
          onClick={() => setOpen(true)}
          style={{ position: 'fixed', right: 24, bottom: 88, zIndex: 1000, boxShadow: '0 6px 20px rgba(24,144,255,.45)' }}
        />
      </Tooltip>

      <Drawer
        title={(
          <Space size={8}>
            <ThunderboltOutlined style={{ color: '#4e9eff' }} />
            <span>AI 帮助助手</span>
            <Tooltip title={`按套餐限额：${quotaText || '—'}；答复以官方文档为准`}>
              <QuestionCircleOutlined style={{ color: '#888', cursor: 'help' }} data-testid="ai-assistant-help" />
            </Tooltip>
          </Space>
        )}
        placement="right"
        size={430}
        open={open}
        zIndex={2147000010}
        onClose={() => setOpen(false)}
        styles={{ body: { display: 'flex', flexDirection: 'column', padding: 0, background: '#141428' } }}
        extra={(
          <Space size={4}>
            <Tooltip title="新建会话">
              <Button size="small" type="text" icon={<PlusOutlined />} onClick={newSession} data-testid="ai-new-session" />
            </Tooltip>
            <Dropdown
              trigger={['click']}
              menu={{
                items: sessions.length
                  ? sessions.map((s) => ({ key: s.id, label: `${s.title}（${s.messageCount}）` }))
                  : [{ key: 'none', label: '暂无会话', disabled: true }],
                onClick: ({ key }) => key !== 'none' && selectSession(key),
                selectedKeys: currentSessionId ? [currentSessionId] : [],
              }}
            >
              <Tooltip title="历史会话">
                <Button size="small" type="text" icon={<HistoryOutlined />} data-testid="ai-history" />
              </Tooltip>
            </Dropdown>
            {currentSessionId && (
              <Popconfirm title="删除该会话？" onConfirm={() => removeSession(currentSessionId)}>
                <Tooltip title="删除当前会话">
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} data-testid="ai-delete-session" />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        )}
      >
        <div ref={listRef} style={{ flex: 1, overflow: 'auto', padding: '12px 14px' }} data-testid="ai-message-list">
          {messages.length === 0 && !streamingText && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span style={{ color: '#888' }}>选择下方预设问题，或直接输入你的问题</span>}
              style={{ marginTop: 40 }}
            />
          )}
          {messages.map((m) => (
            <div key={m.id} style={{ marginBottom: 12, textAlign: m.role === 'user' ? 'right' : 'left' }}>
              <div
                data-testid={`ai-msg-${m.role}`}
                style={{
                  display: 'inline-block', maxWidth: '88%', textAlign: 'left', padding: '8px 12px',
                  borderRadius: 8, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  background: m.role === 'user' ? '#153a63' : '#1f1f38',
                  border: `1px solid ${m.role === 'user' ? '#1d5c9e' : '#33335a'}`,
                  color: '#e6f4ff',
                }}
              >
                {m.content}
              </div>
              {m.refs && m.refs.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <Text style={{ fontSize: 11, color: '#888' }}>参考来源：</Text>
                  {m.refs.map((r) => (
                    <Button
                      key={r.slug}
                      size="small"
                      type="link"
                      style={{ padding: '0 6px', fontSize: 12 }}
                      data-testid={`ai-ref-${r.slug}`}
                      onClick={() => { navigate(r.url); setOpen(false); }}
                    >
                      {r.title}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {streamingText && (
            <div style={{ marginBottom: 12 }}>
              <div
                data-testid="ai-msg-streaming"
                style={{
                  display: 'inline-block', maxWidth: '88%', padding: '8px 12px', borderRadius: 8, fontSize: 13,
                  lineHeight: 1.7, whiteSpace: 'pre-wrap', background: '#1f1f38', border: '1px solid #33335a', color: '#e6f4ff',
                }}
              >
                {streamingText}
                <span style={{ color: '#4e9eff' }}>▍</span>
              </div>
            </div>
          )}
          {sending && !streamingText && (
            <div style={{ marginBottom: 12 }}><Spin size="small" /> <Text style={{ color: '#888', fontSize: 12 }}>正在思考…</Text></div>
          )}
          {quotaBlocked && !error && (
            <Alert
              type="warning"
              showIcon
              message={quotaZero ? '当前套餐不含 AI 问答' : '今日 AI 问答额度已用完'}
              description={(
                <div style={{ fontSize: 12 }}>
                  升级套餐可提升 AI 问答额度（免费版不含，专业版 10 次/日）。
                  <div style={{ marginTop: 6 }}>
                    <Button size="small" type="primary" onClick={() => { setOpen(false); navigate('/account'); }}>
                      查看套餐 / 升级
                    </Button>
                  </div>
                </div>
              )}
              style={{ marginBottom: 8 }}
              data-testid="ai-quota-banner"
            />
          )}
          {error && (
            <Alert
              type={quotaExceeded ? 'warning' : 'error'}
              showIcon
              closable
              onClose={clearError}
              message={quotaExceeded ? '今日 AI 问答额度已用完' : 'AI 对话失败'}
              description={(
                <div style={{ fontSize: 12 }}>
                  {error}
                  {quotaExceeded && (
                    <div style={{ marginTop: 6 }}>
                      <Button size="small" type="primary" onClick={() => { setOpen(false); navigate('/account'); }}>
                        查看套餐 / 升级
                      </Button>
                    </div>
                  )}
                </div>
              )}
              style={{ marginBottom: 8 }}
              data-testid="ai-error"
            />
          )}
        </div>

        <div style={{ borderTop: '1px solid #33335a', padding: 10 }}>
          <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
            <Select
              style={{ width: '100%' }}
              size="small"
              placeholder="预设问题（可自定义输入）"
              value={preset}
              options={PRESET_QUESTIONS.map((q) => ({ value: q, label: q }))}
              onChange={(v) => { setPreset(v); setInput(v); }}
              data-testid="ai-preset-select"
            />
            <Button size="small" icon={<SendOutlined />} disabled={!preset || quotaBlocked} onClick={() => doSend(preset)}>提问</Button>
          </Space.Compact>
          <Input.TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入问题，Enter 发送 / Shift+Enter 换行"
            autoSize={{ minRows: 1, maxRows: 4 }}
            maxLength={2000}
            data-testid="ai-input"
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                doSend();
              }
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
            <Text style={{ fontSize: 11, color: '#666' }}>{quotaText} · 答复以官方文档为准</Text>
            <Button
              type="primary"
              size="small"
              icon={<SendOutlined />}
              loading={sending}
              disabled={!input.trim() || quotaBlocked}
              onClick={() => doSend()}
              data-testid="ai-send"
            >
              发送
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
}
